import {
  Button,
  cx,
  Input,
  Table,
  TableCard,
} from "@light-technology/components";
import { ArrowLeft, ArrowRight, SearchLg, XClose } from "@untitledui/icons";
import { format } from "date-fns";
import { AnimatePresence, MotionConfig, motion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import accounts from "../../assets/accounts.svg";
import { ACCOUNTS_PAGE_SIZE, api } from "../../state/api";
import {
  PlanType,
  Role,
  type AppAccount,
  type AppResponse,
  type Program,
} from "../../state/api.types";
import { PRESERVE_SCROLL } from "../../utilities/links";
import { formatResultRange, previousOffset } from "../../utilities/pagination";
import { toSearchParams } from "../../utilities/searchParams";
import { useDebounce } from "../../utilities/useDebounce";
import { useDocumentTitle } from "../../utilities/useDocumentTitle";
import { RoleGate } from "../RoleGate";
import { SkeletonLine } from "../shared/SkeletonLine";
import { AccountFiltersSlideout } from "./AccountFiltersSlideout";
import { AccountMetrics } from "./AccountMetrics";
import { AccountPlanPreview } from "./AccountPlanPreview";
import { AccountSlideout } from "./AccountSlideout";
import { AccountStatus } from "./AccountStatus";
import { AccountsEmptyState } from "./AccountsEmptyState";
import { DeviceStatuses } from "./DeviceStatuses";
import { ExportAccountsButton } from "./ExportAccountsButton";
import {
  type AccountFilters,
  getActiveFilterPills,
  hasActiveSlideoutFilters,
  parseStatuses,
  removeFilter,
  toAccountFilterParams,
} from "./accountFilterHelpers";
import {
  parseDevicePrograms,
  parseDeviceStatuses,
} from "./deviceFilterHelpers";

const formatContractEnd = (account: AppAccount): string => {
  const contractEnd = account.locations.at(0)?.contract_end;
  return contractEnd ? format(contractEnd, "PPP") : "—";
};

const CHIP_ENTER = { opacity: 0, x: -6 };
const CHIP_SETTLED = { opacity: 1, x: 0, scale: 1 };
const CHIP_LEAVE = { opacity: 0, scale: 0.9 };
const CHIP_TRANSITION = { duration: 0.18, ease: "easeOut" } as const;

/** Cycled per row so skeleton text looks like real, uneven content. */
const SKELETON_WIDTHS = [
  { name: "w-40", email: "w-56", status: "w-16", plan: "w-44", date: "w-36" },
  { name: "w-32", email: "w-44", status: "w-20", plan: "w-52", date: "w-32" },
  { name: "w-48", email: "w-52", status: "w-16", plan: "w-32", date: "w-36" },
  { name: "w-36", email: "w-60", status: "w-14", plan: "w-48", date: "w-28" },
];

type AccountsPageProps = {
  app: AppResponse;
};

export const AccountsPage = (props: AccountsPageProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  useDocumentTitle("Accounts");

  const [filters, setFilters] = useState<AccountFilters>(() => ({
    planGroupUUIDs: searchParams.getAll("plan_group_uuid"),
    statuses: parseStatuses(searchParams.getAll("filter")),
    fallbackPlanOnly: searchParams.get("plan_type") === PlanType.Fallback,
    devicePrograms: parseDevicePrograms(searchParams.getAll("device_program")),
    deviceStatuses: parseDeviceStatuses(searchParams.getAll("device_status")),
    upcomingExpirationsOnly:
      searchParams.get("upcoming_expirations") === "true",
  }));
  const [offset, setOffset] = useState(Number(searchParams.get("offset")));
  const [search, setSearch] = useState(searchParams.get("search") ?? "");
  const [showLeftFade, setShowLeftFade] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const pageScrollRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const pendingPageScrollRef = useRef(false);

  const handleScroll = () => {
    if (scrollRef.current) {
      setShowLeftFade(scrollRef.current.scrollLeft > 0);
    }
  };

  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    const params: Record<string, string | string[] | undefined> = {};

    if (debouncedSearch) {
      params.search = debouncedSearch;
    }
    if (filters.statuses.length > 0) {
      params.filter = filters.statuses;
    }
    if (offset !== 0) {
      params.offset = offset.toString();
    }
    if (filters.planGroupUUIDs.length > 0) {
      params.plan_group_uuid = filters.planGroupUUIDs;
    }
    if (filters.fallbackPlanOnly) {
      params.plan_type = PlanType.Fallback;
    }
    if (filters.devicePrograms.length > 0) {
      params.device_program = filters.devicePrograms;
    }
    if (filters.deviceStatuses.length > 0) {
      params.device_status = filters.deviceStatuses;
    }
    if (filters.upcomingExpirationsOnly) {
      params.upcoming_expirations = "true";
    }

    setSearchParams(toSearchParams(params));
  }, [setSearchParams, debouncedSearch, filters, offset]);

  const filterParams = toAccountFilterParams(filters);

  const getAccounts = api.useGetAccountsQuery({
    appUUID: props.app.uuid,
    search: debouncedSearch,
    offset,
    ...filterParams,
  });

  const page = getAccounts.data;

  const account_ids = page?.data.map((account) => account.uuid);

  const getProgramAgreements = api.useGetProgramsAgreementsQuery(
    {
      appUUID: props.app.uuid,
      account_ids: account_ids,
    },
    { skip: !page?.data.length },
  );

  const getDevices = api.useGetDevicesQuery(
    {
      appUUID: props.app.uuid,
      account_ids: account_ids,
    },
    { skip: !page?.data.length },
  );

  const devicesByAccount = Map.groupBy(
    getDevices.data?.data ?? [],
    (d) => d.account_uuid,
  );

  const programsByAccount = (getProgramAgreements.data?.data ?? []).reduce<
    Map<string, Program[]>
  >((map, pa) => {
    const list = map.get(pa.account_uuid) ?? [];
    list.push(pa.program);
    map.set(pa.account_uuid, list);
    return map;
  }, new Map());

  const getAccountsCount = api.useGetAccountsCountQuery({
    appUUID: props.app.uuid,
    search: debouncedSearch,
    ...filterParams,
  });

  const getPlans = api.useGetAppPlansQuery(props.app.uuid);
  const plans = getPlans.data?.data ?? [];

  const hasActiveFilters = hasActiveSlideoutFilters(filters) || search !== "";

  const activeFilterPills = getActiveFilterPills(filters, plans);

  const handleApplyFilters = (newFilters: AccountFilters) => {
    setFilters(newFilters);
    setOffset(0);
  };

  /**
   * Turning a page puts the first row back under the header. The scroll is
   * deferred to the effect below because measuring mid-update reads a layout
   * that is about to change, and lands short.
   */
  const goToOffset = (nextOffset: number) => {
    setOffset(nextOffset);
    pendingPageScrollRef.current = true;
  };

  useEffect(() => {
    const scroller = pageScrollRef.current;
    const table = tableRef.current;
    if (!pendingPageScrollRef.current || !scroller || !table) {
      return;
    }
    pendingPageScrollRef.current = false;
    const tableTop =
      table.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
    scroller.scrollTo({ top: scroller.scrollTop + tableTop });
  }, [page?.offset]);

  const handleRemoveFilter = (key: string) => {
    setFilters((prev) => removeFilter(prev, key));
    setOffset(0);
  };

  /*
   * Anything replacing the rows shows skeletons in their place, held at the
   * count last on screen so the table keeps its height across the swap — the
   * page neither jumps nor collapses under the scroll.
   */
  const isFetching =
    getAccounts.isFetching ||
    getDevices.isFetching ||
    getProgramAgreements.isFetching;
  const skeletonRowCount = page?.data.length ?? ACCOUNTS_PAGE_SIZE;

  const isPageChanging =
    getAccounts.isFetching || (page !== undefined && page.offset !== offset);
  const noData = !getAccounts.isFetching && !page?.data.length;

  return (
    <AccountSlideout app={props.app}>
      <div
        ref={pageScrollRef}
        className="h-full overflow-auto scroll-smooth motion-reduce:scroll-auto"
      >
        <div className="flex min-h-full flex-col p-4xl pb-0">
          <div className="mb-7xl flex flex-wrap items-center justify-between gap-xl">
            <div className="flex items-center gap-xl">
              <img
                src={accounts}
                alt="accounts"
                className="h-12 w-12 object-contain"
              />
              <div className="text-display-sm font-semibold">Accounts</div>
            </div>
            <RoleGate roles={[Role.Developer]}>
              <ExportAccountsButton appUUID={props.app.uuid} />
            </RoleGate>
          </div>
          <div className="flex w-full flex-1 flex-col">
            <AccountMetrics
              appUUID={props.app.uuid}
              filters={filters}
              onSelect={handleApplyFilters}
            />
            <div className="relative mb-3xl flex items-center gap-lg">
              {showLeftFade && activeFilterPills.length > 0 && (
                <div className="pointer-events-none absolute top-0 bottom-0 left-0 z-10 hidden w-12 bg-gradient-to-r from-[var(--color-bg-primary)] to-transparent @page-sm:block" />
              )}
              <div
                ref={scrollRef}
                onScroll={handleScroll}
                className="flex min-w-0 flex-1 items-center gap-lg @page-sm:scrollbar-hide @page-sm:overflow-x-auto"
              >
                <div className="shrink-0">
                  <AccountFiltersSlideout
                    appUUID={props.app.uuid}
                    filters={filters}
                    activeFilterCount={activeFilterPills.length}
                    onApply={handleApplyFilters}
                    plans={plans}
                    isPlansLoading={getPlans.isLoading}
                    search={debouncedSearch}
                  />
                </div>
                {/* `popLayout` takes a leaving chip out of the flow as it
                 * fades, so the ones after it close the gap immediately. */}
                <MotionConfig reducedMotion="user">
                  <AnimatePresence initial={false} mode="popLayout">
                    {activeFilterPills.map((pill) => (
                      <motion.div
                        key={pill.key}
                        layout
                        initial={CHIP_ENTER}
                        animate={CHIP_SETTLED}
                        exit={CHIP_LEAVE}
                        transition={CHIP_TRANSITION}
                        className="hidden shrink-0 @page-sm:flex"
                      >
                        <Button
                          size="sm"
                          color="secondary"
                          iconTrailing={XClose}
                          onClick={() => {
                            handleRemoveFilter(pill.key);
                          }}
                        >
                          {pill.label}
                        </Button>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </MotionConfig>
              </div>
              <div className="relative ml-auto shrink-0">
                {activeFilterPills.length > 0 && (
                  <div className="pointer-events-none absolute top-0 bottom-0 -left-12 hidden w-12 bg-gradient-to-r from-transparent to-[var(--color-bg-primary)] @page-sm:block" />
                )}
                <Input
                  className="w-full @page-sm:w-80"
                  type="search"
                  placeholder="Search by name, email, and more"
                  icon={SearchLg}
                  value={search}
                  onChange={(v) => {
                    setSearch(v);
                    setOffset(0);
                  }}
                />
              </div>
            </div>
            {/* Full-bleed: the card drops its rounding and side ring so the
             * table runs to the page edges. */}
            <TableCard.Root className="-mx-4xl flex flex-1 flex-col overflow-visible rounded-none border-t border-secondary shadow-none ring-0">
              {/*
               * `z-0` keeps the sticky header's z-index local to the table, so
               * it cannot rise above a slideout opened over the page.
               *
               * In a narrow content column this box takes the sideways
               * scroll, which keeps the footer from being carried off. The
               * cost is the header: an `overflow` of any kind captures a
               * sticky descendant, so the header only sticks once the column
               * is wide enough to drop the scroll.
               */}
              <div
                ref={tableRef}
                className="relative z-0 flex flex-1 flex-col overflow-x-auto @page-sm:overflow-x-visible"
              >
                {/*
                 * Fixed layout, or the columns resize to whatever the current
                 * page of rows contains and every filter change reflows the
                 * table. The declared widths total 100%; `onRowClick` adds a
                 * trailing actions column with its own width, so anything left
                 * over inflates that column into dead space.
                 */}
                <Table
                  aria-label="Accounts"
                  className="min-w-[55rem] table-fixed [&_tbody>tr>td]:after:bg-[var(--color-border-tertiary)] [&_td:first-child]:pl-4xl [&_td:last-child]:pr-4xl [&_th:first-child]:pl-4xl [&_th:last-child]:pr-4xl"
                  size="sm"
                  onRowClick={(row) => {
                    navigate(
                      `/apps/${props.app.slug}/accounts/${row.id as string}`,
                      PRESERVE_SCROLL,
                    );
                  }}
                >
                  <Table.Header sticky>
                    <Table.Head
                      isRowHeader
                      id="account"
                      label="Account"
                      className="w-[26%]"
                    />
                    <Table.Head
                      id="status"
                      label="Status"
                      className="w-[12%]"
                    />
                    <Table.Head id="plan" label="Plan" className="w-[26%]" />
                    <Table.Head
                      id="devices"
                      label="Devices"
                      className="w-[10%]"
                    />
                    {/*
                     * One column with a swapping label, not two conditional
                     * ones: a column's `id` is its collection key, and changing
                     * it throws "Cannot change the id of an item".
                     */}
                    <Table.Head
                      id="date"
                      label={
                        filters.upcomingExpirationsOnly
                          ? "Expiration date"
                          : "Created"
                      }
                      className="w-[26%] whitespace-nowrap"
                    />
                  </Table.Header>
                  <Table.Body>
                    {isFetching
                      ? Array.from({ length: skeletonRowCount }, (_, i) => {
                          const width =
                            SKELETON_WIDTHS[i % SKELETON_WIDTHS.length];
                          return (
                            <Table.Row
                              key={`skeleton-${String(i)}`}
                              id={`skeleton-${String(i)}`}
                              className="pointer-events-none [&_svg]:text-fg-disabled_subtle"
                            >
                              <Table.Cell>
                                <SkeletonLine
                                  className={cx("text-sm", width.name)}
                                />
                                <SkeletonLine
                                  className={cx("text-sm", width.email)}
                                  subtle
                                />
                              </Table.Cell>
                              <Table.Cell>
                                <SkeletonLine
                                  className={cx("text-sm", width.status)}
                                  subtle
                                />
                              </Table.Cell>
                              <Table.Cell>
                                <SkeletonLine
                                  className={cx("text-sm", width.plan)}
                                  subtle
                                />
                              </Table.Cell>
                              <Table.Cell>
                                {/* The devices column renders round icons. */}
                                <span className="block size-8 animate-pulse rounded-full bg-fg-disabled_subtle" />
                              </Table.Cell>
                              <Table.Cell>
                                <SkeletonLine
                                  className={cx("text-sm", width.date)}
                                  subtle
                                />
                              </Table.Cell>
                            </Table.Row>
                          );
                        })
                      : noData
                        ? []
                        : (page?.data ?? []).map((account) => (
                            <Table.Row key={account.uuid} id={account.uuid}>
                              <Table.Cell>
                                {account.first_name || account.last_name ? (
                                  <>
                                    <div className="text-sm font-medium text-primary">
                                      {[account.first_name, account.last_name]
                                        .filter(Boolean)
                                        .join(" ")}
                                    </div>
                                    <div className="truncate text-sm text-tertiary">
                                      {account.email}
                                    </div>
                                  </>
                                ) : (
                                  <div className="truncate text-sm font-medium text-primary">
                                    {account.email}
                                  </div>
                                )}
                              </Table.Cell>
                              <Table.Cell>
                                <AccountStatus account={account} showTooltip />
                              </Table.Cell>
                              <Table.Cell>
                                {account.locations.map((l) => (
                                  <AccountPlanPreview
                                    key={l.uuid}
                                    location={l}
                                    showDescription={false}
                                    truncateName
                                  />
                                ))}
                              </Table.Cell>
                              <Table.Cell>
                                <DeviceStatuses
                                  devices={
                                    devicesByAccount.get(account.uuid) ?? []
                                  }
                                  programs={
                                    programsByAccount.get(account.uuid) ?? []
                                  }
                                />
                              </Table.Cell>
                              <Table.Cell className="whitespace-nowrap">
                                {filters.upcomingExpirationsOnly
                                  ? formatContractEnd(account)
                                  : format(account.created_at, "PPP")}
                              </Table.Cell>
                            </Table.Row>
                          ))}
                  </Table.Body>
                </Table>
                {noData && !isFetching && (
                  <div className="sticky left-0 flex w-full flex-1 items-center justify-center p-5xl">
                    <AccountsEmptyState hasActiveFilters={hasActiveFilters} />
                  </div>
                )}
              </div>
            </TableCard.Root>
            {/* Pinned so the page controls stay reachable through a long page
             * of rows; the negative margin spans the page gutter. */}
            <div className="sticky bottom-0 left-0 -mx-4xl flex items-center gap-lg border-t border-secondary bg-primary px-4xl py-lg">
              <Button
                color="secondary"
                size="sm"
                iconLeading={ArrowLeft}
                isDisabled={isPageChanging || offset === 0}
                onClick={() => {
                  if (page) {
                    goToOffset(previousOffset(offset, page.limit));
                  }
                }}
              >
                Previous
              </Button>
              <Button
                color="secondary"
                size="sm"
                iconTrailing={ArrowRight}
                isDisabled={isPageChanging || !page?.has_more}
                onClick={() => {
                  if (page) {
                    goToOffset(offset + page.limit);
                  }
                }}
              >
                Next
              </Button>
              <div className="ml-auto font-mono text-sm text-secondary">
                {getAccountsCount.data && page && !isFetching ? (
                  formatResultRange(
                    page.offset,
                    page.data.length,
                    getAccountsCount.data.count,
                  )
                ) : (
                  <SkeletonLine className="w-36 text-sm" subtle />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AccountSlideout>
  );
};
